import { ANALYTICS_CONFIG } from './config';

class AnalyticsManager {
  constructor() {
    this.isInitialized = false;
    this.eventQueue = [];
    this.userId = null;
    this.sessionId = null;
    this.gaClientId = null;
  }

  // Safe localStorage wrapper
  safeLocalStorage = {
    getItem: (key) => {
      if (typeof window === 'undefined') return null;
      try {
        return localStorage.getItem(key);
      } catch (e) {
        console.warn('localStorage getItem failed:', e);
        return null;
      }
    },
    setItem: (key, value) => {
      if (typeof window === 'undefined') return;
      try {
        localStorage.setItem(key, value);
      } catch (e) {
        console.warn('localStorage setItem failed:', e);
      }
    }
  };

  // Extract GA Client ID from cookies or gtag
  async getGAClientId() {
    if (typeof window === 'undefined') return null;

    try {
      // Method 1: Get from gtag if available
      if (window.gtag && typeof window.gtag === 'function') {
        return new Promise((resolve) => {
          window.gtag('get', ANALYTICS_CONFIG.GA4_MEASUREMENT_ID, 'client_id', (clientId) => {
            resolve(clientId || this.generateClientId());
          });
        });
      }

      // Method 2: Extract from _ga cookie
      const gaCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('_ga='));
      
      if (gaCookie) {
        // _ga cookie format: GA1.2.clientId.timestamp or GA1.2.random1.random2
        const parts = gaCookie.split('=')[1].split('.');
        if (parts.length >= 4) {
          // Client ID is the 3rd and 4th parts joined with a dot
          return `${parts[2]}.${parts[3]}`;
        }
      }

      // Method 3: Generate a fallback client ID
      return this.generateClientId();
    } catch (error) {
      console.warn('Failed to get GA Client ID:', error.message);
      return this.generateClientId();
    }
  }

  // Generate a client ID similar to GA format
  generateClientId() {
    const timestamp = Math.floor(Date.now() / 1000);
    const random = Math.floor(Math.random() * 2147483647) + 1;
    return `${random}.${timestamp}`;
  }

  // Get existing user ID from capturePageHit.js system
  generateUserId() {
    if (typeof window === 'undefined') return null;
    
    try {
      // Use the existing user_id from capturePageHit.js (stored as JSON string)
      const existingId = JSON.parse(localStorage.getItem('user_id'));
      if (existingId) return existingId;
      
      // Fallback: generate similar format if not found
      const newUserId = "_" + Math.random()?.toString(36).substr(2, 9);
      localStorage.setItem('user_id', JSON.stringify(newUserId));
      return newUserId;
    } catch (error) {
      console.warn('Failed to get user ID:', error.message);
      return null;
    }
  }

  // Generate session ID
  generateSessionId() {
    if (typeof window === 'undefined') return null;
    
    const existingSessionId = sessionStorage.getItem('turno_session_id');
    if (existingSessionId) return existingSessionId;
    
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substr(2, 9);
    const newSessionId = `session_${timestamp}_${randomStr}`;
    
    sessionStorage.setItem('turno_session_id', newSessionId);
    return newSessionId;
  }

  // Initialize analytics
  async init() {
    if (this.isInitialized) return;
    
    try {
      // Set user and session IDs
      this.userId = this.generateUserId();
      this.sessionId = this.generateSessionId();
      
      // Get GA Client ID
      this.gaClientId = await this.getGAClientId();
      
      // Initialize GTM if enabled
      console.log('📊 Analytics Config:', {
        ENABLE_GTM: ANALYTICS_CONFIG.ENABLE_GTM,
        ENABLE_GA4: ANALYTICS_CONFIG.ENABLE_GA4,
        GTM_CONTAINER_ID: ANALYTICS_CONFIG.GTM_CONTAINER_ID,
        GA4_MEASUREMENT_ID: ANALYTICS_CONFIG.GA4_MEASUREMENT_ID
      });
      
      if (ANALYTICS_CONFIG.ENABLE_GTM && typeof window !== 'undefined') {
        console.log('📊 Initializing GTM...');
        await this.initGTM();
      }
      
      // Initialize GA4 if enabled
      if (ANALYTICS_CONFIG.ENABLE_GA4 && typeof window !== 'undefined') {
        console.log('📊 Initializing GA4...');
        await this.initGA4();
      }
      
      // Initialize Amplitude if enabled and API key is provided
      if (ANALYTICS_CONFIG.ENABLE_AMPLITUDE && ANALYTICS_CONFIG.AMPLITUDE_API_KEY && typeof window !== 'undefined') {
        await this.initAmplitude();
      }
      
      this.isInitialized = true;
      
      
      // Process queued events
      this.processEventQueue();
      
    } catch (error) {
      console.error('Analytics initialization failed:', error);
    }
  }

  // Initialize GTM
  async initGTM() {
    console.log('🏷️ Initializing GTM:', ANALYTICS_CONFIG.GTM_CONTAINER_ID);
    
    if (window.dataLayer && window.dataLayer.length > 0) {
      console.log('🏷️ GTM already initialized');
      return;
    }
    
    // Load GTM script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${ANALYTICS_CONFIG.GTM_CONTAINER_ID}`;
    document.head.appendChild(script);
    
    console.log('🏷️ GTM script loaded:', script.src);
    
    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });
    
    console.log('🏷️ GTM dataLayer initialized');
  }

  // Initialize GA4
  async initGA4() {
    if (window.gtag) return; // Already initialized via GTM
    
    // Only initialize standalone GA4 if GTM is not enabled
    if (!ANALYTICS_CONFIG.ENABLE_GTM) {
      // Load GA4 script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${ANALYTICS_CONFIG.GA4_MEASUREMENT_ID}`;
      document.head.appendChild(script);
      
      // Initialize gtag
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', ANALYTICS_CONFIG.GA4_MEASUREMENT_ID);
    }
  }

  // Initialize Amplitude - DISABLED
  async initAmplitude() {
    // Amplitude is disabled to avoid build errors
    return;
  }

  // Track event
  track(eventName, properties = {}) {
    const event = {
      eventName,
      properties: {
        ...properties,
        user_id: this.userId,
        session_id: this.sessionId,
        ga_client_id: this.gaClientId,
        visitor_id: this.gaClientId, // Alternative name for GA client ID
        timestamp: new Date().toISOString(),
        page_url: typeof window !== 'undefined' ? window.location.href : '',
        page_title: typeof document !== 'undefined' ? document.title : '',
        referrer: typeof document !== 'undefined' ? document.referrer : '',
      }
    };

    if (!this.isInitialized) {
      this.eventQueue.push(event);
      return;
    }

    this.sendEvent(event);
  }

  // Send event to all platforms
  sendEvent(event) {
    try {
      // Send to GTM/GA4
      if (window.gtag && (ANALYTICS_CONFIG.ENABLE_GTM || ANALYTICS_CONFIG.ENABLE_GA4)) {
        window.gtag('event', event.eventName, event.properties);
      }

      // Send to Amplitude (disabled for now)
      // if (window.amplitude && ANALYTICS_CONFIG.ENABLE_AMPLITUDE) {
      //   try {
      //     window.amplitude.getInstance().logEvent(event.eventName, event.properties);
      //   } catch (error) {
      //     console.warn('Amplitude tracking failed:', error.message);
      //   }
      // }


    } catch (error) {
      console.error('Failed to send analytics event:', error);
    }
  }

  // Process queued events
  processEventQueue() {
    while (this.eventQueue.length > 0) {
      const event = this.eventQueue.shift();
      this.sendEvent(event);
    }
  }

}

// Create singleton instance
const analyticsManager = new AnalyticsManager();

export default analyticsManager;
